
import { Vue, Component } from 'vue-property-decorator';
import Pagination from '@/components/Pagination/index.vue';
import {queryPoDeliveryTrack} from "@/api/proforma";
import Template from "@/views/system-management/template/index.vue";
import {getSuppliersList} from "@/api/inquiry";

interface PoDeliveryTrackingRow {
  id: string;
  code: string;
  supplierAbbr: string;
  purchaser: string;
  status: { name: string; desc: string };
  orderDate: string;
  deliveryDate: string;
  remainDeliveryDays: number;
  overdueDays: number;
  orderAmount: number;
  deliveryAmount: number;
  orderQty: number;
  deliveryQty: number;
  amountRate?: number;
  quantityRate?: number;
}

interface CustomerInfo {
  customerCode?: string;
  customerAbbr?: string;
  seller?: string;
}

interface PiRow {
  id: string;
  code: string;
  overdueAndNotCompletePoCount: number;
}

interface ListQueryModel {
  codeKeyword: string;
  supplierId: string;
}

@Component({
  name: 'PoDeliveryTracking',
  components: {Template, Pagination },
})
export default class extends Vue {
  public visible = false;
  private piInfo: PiRow = {
    id: '',
    code: '',
    overdueAndNotCompletePoCount: 0,
  };
  private customerInfo: CustomerInfo = {};
  private tableData: PoDeliveryTrackingRow[] = [];
  private tableDataLoading = false;
  private pageNumber = 1;
  private pageSize = 20;
  private pageTotal = 0;
  private totalCount = 0;
  private overdueIncompleteCount = 0;
  public listQuery: ListQueryModel = {
    codeKeyword: '',
    supplierId: '',
  };
  private supplierOptions = [];

  get customerLabel(): string {
    const code = this.customerInfo.customerCode || '--';
    const abbr = this.customerInfo.customerAbbr || '';
    return abbr ? `${code} ${abbr}` : code;
  }

  get piCode(): string {
    return this.piInfo.code || '--';
  }

  get sellerLabel(): string {
    return this.customerInfo.seller || '--';
  }

  public open(row: PiRow, customerInfo?: CustomerInfo): void {
    this.piInfo = row;
    this.customerInfo = customerInfo || {};
    this.overdueIncompleteCount = this.piInfo.overdueAndNotCompletePoCount ?? 0;
    this.listQuery = { codeKeyword: '', supplierId: '' };
    this.pageNumber = 1;
    this.visible = true;
    this.fetchData();
  }

  public close(): void {
    this.visible = false;
  }

  private async getSuppliersList(keyword = "") {
    this.supplierOptions = await getSuppliersList({ abbreviation: keyword }) as any;
  }

  public handleSearch(): void {
    this.pageNumber = 1;
    this.fetchData();
  }

  private async fetchData(): Promise<void> {
    this.tableDataLoading = true;
    try {
      const params = {
        piId: this.piInfo.id,
        ...this.listQuery,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
      };
      const res = await queryPoDeliveryTrack(params) as any;
      const list = res.list.map(item => {
        return {
          supplierAbbr: item.supplierAbbr,
          purchaser: item.purchaser,
          ...item.poDeliveryTrack
        }
      });
      this.tableData = list.map(this.mapRowRates);
      this.pageTotal = Number(res.total);
      this.totalCount = res.total;
    } finally {
      this.tableDataLoading = false;
    }
  }

  private mapRowRates(item: PoDeliveryTrackingRow): PoDeliveryTrackingRow {
    const amountRate = item.orderAmount
        ? Math.floor((item.deliveryAmount / item.orderAmount) * 100)
        : 0;
    const quantityRate = item.orderQty
        ? Math.floor((item.deliveryQty / item.orderQty) * 100)
        : 0;
    return { ...item, amountRate, quantityRate };
  }

  public statusTagType(status: string): string {
    if (status === 'DELIVERY_COMPLETED' || status === 'COMPLETED') {
      return 'success';
    }
    if (status === 'PARTIALLY_DELIVERED') {
      return 'warning';
    }
    if (status === 'NOT_DELIVERED') {
      return 'info';
    }
    return '';
  }

  public progressColor(value: number): string {
    if (value >= 100) {
      return '#67c23a';
    }
    if (value >= 60) {
      return '#e6a23c';
    }
    return '#f56c6c';
  }

  public getRemainingDays(row: PoDeliveryTrackingRow): number | null {
    if (!row.deliveryDate) {
      return null;
    }
    if (row.status.name === 'DELIVERY_COMPLETED' || row.status.name === 'COMPLETED') {
      return null;
    }
    const days = row.remainDeliveryDays;
    if (days === null) {
      return null;
    }
    return days > 0 ? days : 0;
  }

  public isOverdue(row: PoDeliveryTrackingRow) {
    if (!row.deliveryDate) {
      return 0;
    }
    const days = row.overdueDays;
    return days && days > 0 ? days : 0;
  }

  private getDeliveryDate(row: PoDeliveryTrackingRow) {
    if (row.status.name !== "DELIVERY_COMPLETED") {
      return row.deliveryDate? row.deliveryDate : "--";
    } else {
      if (row.deliveryDate) {
        return row.deliveryDate;
      } else {
        return "error";
      }
    }
  }

  private getRemainingDaysText(row: PoDeliveryTrackingRow) {
    if (row.status.name !== "DELIVERY_COMPLETED") {
      if (row.deliveryDate) {
        if (row.remainDeliveryDays != null) {
          return row.remainDeliveryDays >= 0 ? row.remainDeliveryDays : "";
        } else {
          return "error";
        }
      } else {
        return "--";
      }
    } else {
      return "";
    }
  }

  public getOverdueDaysText(row: PoDeliveryTrackingRow) {
    if (row.status.name !== "DELIVERY_COMPLETED") {
      if (row.deliveryDate) {
        if (row.overdueDays != null) {
          return row.overdueDays >= 0 ? row.overdueDays : "";
        } else {
          return "error";
        }
      } else {
        return "--";
      }
    } else {
      if (row.deliveryDate) {
        if (row.overdueDays != null) {
          return row.overdueDays > 0 ? row.overdueDays : "";
        } else {
          return "noDate";
        }
      } else {
        return "error";
      }
    }
  }

  public remainingDayClass(row: PoDeliveryTrackingRow): string {
    const days = this.getRemainingDaysText(row);
    if (days >= 0 && days <= 10) {
      return "text-warning";
    }
    return "";
  }

  public overdueDayClass(row: PoDeliveryTrackingRow): string {
    const overdueDays = this.getOverdueDaysText(row);
    if (row.status.name !== "DELIVERY_COMPLETED" && overdueDays > 0) {
      return "text-danger";
    }
    if (row.status.name === "DELIVERY_COMPLETED" && overdueDays > 0) {
      return "text-muted";
    }
    return "";
  }

  public isNearlyDue(row: PoDeliveryTrackingRow): boolean {
    const days = this.getRemainingDaysText(row);
    return days >= 0 && days <= 10;
  }

  public getRowClassName({ row }: { row: PoDeliveryTrackingRow }): string {
    if (this.isOverdue(row)) {
      return "row-danger";
    }
    if (this.isNearlyDue(row)) {
      return "row-warning";
    }
    return "";
  }
}
